import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, AlertTriangle, Package, CheckCircle, XCircle, Search, Loader2, Trash2, Check, StickyNote, X, Pencil, Plus, Truck } from 'lucide-react';
import { getConfidenceStyle, formatCurrency, formatUnitPrice, sanitizeErpText, deriveInItembal } from '../../utils/formatting';
import PriceResolutionModal from './PriceResolutionModal';
import ItemSearchModal from './ItemSearchModal';
import { apiService } from '../../services/apiService';
import { trackEvent } from '../../services/telemetry';
import AddLineItemModal from './AddLineItemModal';
import { PRINT_CODES } from './OrderComments';

// Stocking UOM validation (mirrors App.jsx helpers)
const FALLBACK_UOM_CANONICAL_LI = {
  'RL': 'RL', 'ROL': 'RL', 'ROLL': 'RL', 'BX': 'BX', 'BOX': 'BX',
  'EA': 'EA', 'EACH': 'EA', 'PC': 'EA', 'PCS': 'EA', 'UN': 'EA', 'UNIT': 'EA',
  'CS': 'CS', 'CASE': 'CS', 'CA': 'CS', 'CT': 'CT', 'CTN': 'CT', 'CARTON': 'CT',
  'PK': 'PK', 'PKG': 'PK', 'PACK': 'PK', 'PACKAGE': 'PK',
  'DZ': 'DZ', 'DOZ': 'DZ', 'DOZEN': 'DZ', 'FT': 'FT', 'FOOT': 'FT', 'FEET': 'FT',
  'IN': 'IN', 'INCH': 'IN', 'INCHES': 'IN', 'LB': 'LB', 'LBS': 'LB', 'POUND': 'LB', 'POUNDS': 'LB',
  'OZ': 'OZ', 'OUNCE': 'OZ', 'OUNCES': 'OZ', 'GAL': 'GAL', 'GALLON': 'GAL', 'GALLONS': 'GAL',
  'QT': 'QT', 'QUART': 'QT', 'QUARTS': 'QT', 'PT': 'PT', 'PINT': 'PT', 'PINTS': 'PT',
  'M': 'M', 'THOUSAND': 'M', '1000 EA': 'M',
};
function normalizeUomLI(uom) {
  if (!uom) return '';
  return FALLBACK_UOM_CANONICAL_LI[uom.trim().toUpperCase()] || uom.trim().toUpperCase();
}
function isValidStockingUomLI(uom, item) {
  if (!uom || !item.unitOfMeasure) return true;
  const normalized = normalizeUomLI(uom);
  const stockingUoms = [item.unitOfMeasure, item.stockingUom2, item.stockingUom3].filter(u => u && u.trim());
  if (stockingUoms.length === 0) return true;
  return stockingUoms.some(su => normalizeUomLI(su) === normalized);
}

export default function LineItems({ selectedItems, itemsRevalidating, itemRevalidationFailed, inventoryLoading, validationResult, selectedShipTo, user, correlationId, onUpdateItemSelection, onConfirmItem, onUpdateItemQuantity, onValidatePrice, onResolvePrice, onDeleteLineItem, onUpdateNote, onUpdateNotePrintCode, onUpdateItemVendor, headerDropShip, warehouseId, onAddLineItem, canAddLineItem, selectedCustomer }) {
  const [itemDropdowns, setItemDropdowns] = useState({});
  const [resolutionModal, setResolutionModal] = useState(null); // { id, mode } or null
  const [searchModal, setSearchModal] = useState(null); // { id } or null
  const [confirmDelete, setConfirmDelete] = useState(null); // index or null
  const [qtyInputs, setQtyInputs] = useState({}); // { [index]: string } — buffered qty input values
  const [noteEditing, setNoteEditing] = useState({}); // { [index]: string } — buffered note input values
  const noteEscapedRef = useRef(false); // tracks Escape key to prevent onBlur from saving
  const [qtyEditing, setQtyEditing] = useState({}); // { [index]: string } — inline qty edit buffer
  const qtyEscapedRef = useRef(false); // tracks Escape key to prevent onBlur from saving
  const [addLineModal, setAddLineModal] = useState(false);
  // Drop-ship vendor UI, keyed by stable item._id (survives index shifts):
  const [vendorEditing, setVendorEditing] = useState({}); // { [id]: string } buffer while editing
  const [vendorError, setVendorError] = useState({});     // { [id]: string } inline validation error
  const [vendorChecking, setVendorChecking] = useState({}); // { [id]: true } while validating
  const vendorFetchRef = useRef(new Set()); // ids with an in-flight default-vendor resolve
  const vendorEscapedRef = useRef(false);   // Escape guard so onBlur doesn't re-commit
  // Latest items, so async vendor callbacks resolve the CURRENT index of a line
  // (indices shift when a lower line is deleted mid-request).
  const selectedItemsRef = useRef(selectedItems);
  useEffect(() => { selectedItemsRef.current = selectedItems; });

  const dropObjKey = (obj, key) =>
    Object.fromEntries(Object.entries(obj).filter(([k]) => k !== String(key)));

  // Lazily resolve each drop-ship line's default A+ vendor (once per line).
  useEffect(() => {
    selectedItems.forEach((item) => {
      const isDropShipLine = item.isDropShip || headerDropShip;
      // Skip if not applicable, already resolved, or the CSR has manually set a
      // vendor (don't auto-resolve over a deliberate choice).
      if (!isDropShipLine || !item.selectedItemNumber || item.vendorResolved || item.vendorManuallySet) return;
      if (vendorFetchRef.current.has(item._id)) return;
      vendorFetchRef.current.add(item._id);
      apiService.lookupVendor({ itemNumber: item.selectedItemNumber, warehouseId, correlationId })
        .then((res) => {
          // A CSR change may have landed while this lookup was in flight — never
          // clobber a manually-set vendor with the auto-resolved default.
          const cur = selectedItemsRef.current.find(it => it._id === item._id);
          if (!cur || cur.vendorManuallySet) return;
          const idx = selectedItemsRef.current.findIndex(it => it._id === item._id);
          if (idx !== -1) onUpdateItemVendor(idx, { vendorNumber: res.vendorNumber, vendorName: res.vendorName });
        })
        .catch(() => {
          // Mark resolved (blank) so we don't retry every render; CSR can still enter one.
          const cur = selectedItemsRef.current.find(it => it._id === item._id);
          if (!cur || cur.vendorManuallySet) return;
          const idx = selectedItemsRef.current.findIndex(it => it._id === item._id);
          if (idx !== -1) onUpdateItemVendor(idx, { vendorNumber: null, vendorName: null });
        })
        .finally(() => { vendorFetchRef.current.delete(item._id); });
    });
  }, [selectedItems, headerDropShip, warehouseId, correlationId, onUpdateItemVendor]);

  // Commit a CSR-entered vendor number: validate against VENDR, save on success,
  // show an inline error (without clobbering the prior value) on a miss.
  const commitVendorEdit = (item) => {
    const raw = (vendorEditing[item._id] || '').trim();
    if (!raw) { setVendorEditing(prev => dropObjKey(prev, item._id)); return; }
    setVendorChecking(prev => ({ ...prev, [item._id]: true }));
    setVendorError(prev => dropObjKey(prev, item._id));
    apiService.lookupVendor({ vendorNumber: raw, correlationId })
      .then((res) => {
        if (res.exists) {
          const idx = selectedItemsRef.current.findIndex(it => it._id === item._id);
          if (idx !== -1) onUpdateItemVendor(idx, { vendorNumber: res.vendorNumber, vendorName: res.vendorName, manual: true });
          setVendorEditing(prev => dropObjKey(prev, item._id));
        } else {
          setVendorError(prev => ({ ...prev, [item._id]: `Vendor "${raw}" not found in A+` }));
        }
      })
      .catch(() => {
        setVendorError(prev => ({ ...prev, [item._id]: 'Could not validate vendor — try again' }));
      })
      .finally(() => setVendorChecking(prev => dropObjKey(prev, item._id)));
  };

  // Clear stale state when items are removed (indices shift after deletion)
  useEffect(() => {
    setConfirmDelete(null);
    setItemDropdowns({});
    setNoteEditing({});
    setQtyEditing({});
    setQtyInputs({});
    // Close a modal if the line it was opened for is gone (matched by stable id, not index —
    // an in-bounds shift after deleting a lower line leaves the index valid but pointing at
    // the wrong item).
    if (resolutionModal && !selectedItems.some(it => it._id === resolutionModal.id)) {
      setResolutionModal(null);
    }
    if (searchModal && !selectedItems.some(it => it._id === searchModal.id)) {
      setSearchModal(null);
    }
  }, [selectedItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const customerNumber = validationResult?.shipto_validations?.val_shipto_customer_number;

  return (
    <div className="bg-slate-800 rounded-xl p-4 relative">
      {itemsRevalidating && (
        <div className="absolute inset-0 bg-slate-800/80 rounded-xl flex flex-col items-center justify-center z-10">
          <Loader2 className="w-8 h-8 text-amber-400 animate-spin mb-3" />
          <span className="text-sm text-slate-300">Re-validating items for new customer...</span>
        </div>
      )}
      {itemRevalidationFailed && !itemsRevalidating && selectedItems.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8">
          <AlertTriangle className="w-8 h-8 text-red-400 mb-3" />
          <span className="text-sm text-red-400">Failed to re-validate items for the new customer.</span>
          <span className="text-xs text-slate-500 mt-1">Try changing the customer again or reload the page.</span>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
          <Package className="w-3 h-3" />
          Line Items ({selectedItems.length})
          {inventoryLoading && (
            <span className="text-xs text-amber-400 animate-pulse ml-2">Rechecking inventory...</span>
          )}
        </h3>
        <button
          onClick={() => setAddLineModal(true)}
          disabled={!canAddLineItem}
          title={!canAddLineItem ? 'Resolve customer and ship-to before adding items' : 'Add a line item'}
          className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-md transition-colors ${
            canAddLineItem
              ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 border border-amber-500/30'
              : 'text-slate-500 cursor-not-allowed border border-slate-600'
          }`}
        >
          <Plus className="w-3 h-3" />
          Add Line Item
        </button>
      </div>

      <div className="space-y-3">
        {selectedItems.map((item, index) => {
          const style = getConfidenceStyle(item.confidenceLevel, item.confidence);
          const Icon = style.icon;

          const isHighConfidence = item.confidenceLevel === 'high' || item.confidence >= 0.81;
          const isUnmatched = !item.selectedItemNumber;
          const matchedByDescription = !!item.issue && item.issue.includes('matched by description');
          const filteredAlts = item.alternatives?.filter(a => a.item_number !== item.selectedItemNumber) || [];
          const altCount = filteredAlts.length;

          // UOM conversion for ERP contract pricing
          // Rely on backend's price_issue for mismatch detection (backend normalizes UOMs)
          const poUom = (item.poUnitOfMeasure || '').toUpperCase();
          const priceUom = (item.valPriceUom || '').toUpperCase();
          const uomMismatch = item.priceIssue?.includes('UOM Mismatch') || false;
          const contractUom = item.valPriceUom || item.poUnitOfMeasure;
          let contractQty = item.poQuantity;
          // Normalize both sides before comparing: the PO unit and the A+ pricing UOM can be
          // the same unit spelled differently (e.g. PO "ROLL" vs pricing "RL"). A raw string
          // compare treated them as different and applied valPriceUomConversion, inflating the
          // line total by the conversion factor (e.g. 140 ROLL @ $17.75 shown as $9,940 = 560×,
          // not $2,485). Only convert when they are genuinely different units.
          if (!uomMismatch && priceUom && normalizeUomLI(poUom) !== normalizeUomLI(priceUom) && item.valPriceUomConversion) {
            contractQty = item.poQuantity * item.valPriceUomConversion;
          }

          // Any non-zero price difference from the A+ contract (not just >2%). Some customers
          // reject invoices that aren't penny-exact, so the CSR must resolve every difference
          // before submit (Tonya, 2026-07-22). 'exact' (0%) needs no action; 'warning' (<=2%)
          // and 'invalid' (>2%) both require a resolution.
          const hasPriceVariance = !uomMismatch
            && (item.priceVarianceLevel === 'warning' || item.priceVarianceLevel === 'invalid')
            && item.valUnitPrice != null && item.priceVariance != null;

          // "Set up in ITEMBAL" = an ITBAL row exists at the warehouse (backend in_itembal),
          // which is distinct from having stock. TRI-STATE: true / false / null = the ITBAL
          // lookup couldn't answer (pool down, query timeout, or no warehouse resolved). Unknown
          // gets a neutral note and never the red badge — a swallowed SQL failure must not read
          // as a definitive claim that the item isn't set up. onHand drives in-stock vs
          // out-of-stock. `undefined` (mock/legacy, no flag) keeps the old inventory fallback.
          const itembalState = deriveInItembal(item.inItembal, item.inventory);
          const isInItembal = itembalState === true;
          const itembalUnknown = itembalState === null;
          const onHand = item.inventory?.quantity_on_hand ?? 0;

          // Invalid stocking UOM — PO UOM is not IMUNM1/2/3
          const effectiveUom = item.resolution ? item.resolution.uom : item.poUnitOfMeasure;
          const hasInvalidStockingUom = item.selectedItemNumber
            && effectiveUom
            && !isValidStockingUomLI(effectiveUom, item)
            && !uomMismatch;  // Don't duplicate if already caught by price validator UOM mismatch

          const isQtyEditable = item.selectedItemNumber && !item.resolution && item.poQuantity != null
            && item.priceIssue !== 'Validating pricing...';

          // "PO Qty" reference row: normally the originally-extracted qty (so edited lines show their
          // before-value). For a manual-entry line (CIRCLE ONE / handwritten — no extracted qty), the
          // CSR-entered value IS the PO quantity, so show that instead of a blank "—" and $0.00.
          const poQtyDisplay = item.extQuantity != null ? item.extQuantity : item.poQuantity;

          const commitQtyEdit = () => {
            const raw = qtyEditing[index];
            if (raw === undefined) return;
            const val = parseFloat(raw);
            if (!isNaN(val) && val > 0 && val !== item.poQuantity) {
              onUpdateItemQuantity(index, val);
              onValidatePrice(index, { quantity: val });
            }
            setQtyEditing(prev => {
              const next = { ...prev };
              delete next[index];
              return next;
            });
          };

          const renderEditableQty = (value, uom) => {
            if (qtyEditing[index] !== undefined) {
              return (
                <span className="inline-flex items-center gap-1">
                  <input
                    type="text"
                    inputMode="decimal"
                    autoFocus
                    disabled={item.priceIssue === 'Validating pricing...'}
                    value={qtyEditing[index]}
                    className="w-20 px-1.5 py-0.5 bg-slate-700 border border-amber-500/40 rounded text-sm text-white text-right focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    onChange={e => {
                      const raw = e.target.value.replace(/[^0-9.]/g, '');
                      setQtyEditing(prev => ({ ...prev, [index]: raw }));
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') commitQtyEdit();
                      else if (e.key === 'Escape') {
                        qtyEscapedRef.current = true;
                        setQtyEditing(prev => {
                          const next = { ...prev };
                          delete next[index];
                          return next;
                        });
                      }
                    }}
                    onBlur={() => {
                      if (qtyEscapedRef.current) { qtyEscapedRef.current = false; return; }
                      commitQtyEdit();
                    }}
                  />
                  <span>{uom}</span>
                </span>
              );
            }

            if (isQtyEditable) {
              return (
                <span
                  className="cursor-pointer hover:text-amber-400 transition-colors group/qty inline-flex items-center gap-1"
                  onClick={() => setQtyEditing(prev => ({ ...prev, [index]: String(value) }))}
                  title="Click to edit quantity"
                >
                  {value} {uom}
                  <Pencil className="w-3 h-3 opacity-0 group-hover/qty:opacity-100 text-amber-400 transition-opacity" />
                </span>
              );
            }

            return <span>{value ?? '—'} {uom}</span>;
          };

          return (
            <div key={index} id={`line-item-${index}`} className={`p-3 rounded-lg ${style.bg} border ${style.border}`}>
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-slate-400 text-sm">Line {item.poLineNumber || (index + 1)}</span>
                  <Icon className={`w-4 h-4 ${style.color}`} />
                  <span className={`text-xs ${style.color}`}>
                    {Math.round(item.confidence * 100)}% match
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (item.note) {
                        // Note exists — toggle editing
                        setNoteEditing(prev => prev[index] !== undefined
                          ? Object.fromEntries(Object.entries(prev).filter(([k]) => k !== String(index)))
                          : { ...prev, [index]: item.note }
                        );
                      } else {
                        // No note — toggle editor
                        setNoteEditing(prev => prev[index] !== undefined
                          ? Object.fromEntries(Object.entries(prev).filter(([k]) => k !== String(index)))
                          : { ...prev, [index]: '' }
                        );
                      }
                    }}
                    className={`flex items-center gap-1 text-xs transition-colors ${
                      item.note
                        ? 'text-amber-400 hover:text-amber-300'
                        : 'text-slate-400 hover:text-slate-300'
                    }`}
                    title={item.note ? 'Edit line comment' : 'Add a comment to this line item'}
                  >
                    <StickyNote className="w-3 h-3" />
                    {item.note ? 'Comment \u2713' : 'Line Comment'}
                  </button>
                  <div className="w-px h-3.5 bg-slate-600/50" />
                  {confirmDelete === index ? (
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className="text-red-400">Delete?</span>
                      <button
                        onClick={() => {
                          onDeleteLineItem(index);
                          setConfirmDelete(null);
                        }}
                        className="px-1.5 py-0.5 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded transition-colors"
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="px-1.5 py-0.5 bg-slate-700 text-slate-400 hover:bg-slate-600 rounded transition-colors"
                      >
                        No
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(index)}
                      className="text-slate-600 hover:text-red-400 transition-colors"
                      title="Remove line item"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {isHighConfidence && (
                    <button
                      onClick={() => { trackEvent('csr_item_search', { correlationId, lineIndex: index }); setSearchModal({ id: item._id }); }}
                      className="text-xs text-slate-500 hover:text-amber-400 transition-colors"
                    >
                      Change
                    </button>
                  )}
                </div>
              </div>

              {/* Item info */}
              <div>
                <div className="font-mono text-white">{item.selectedItemNumber || 'Not matched'}</div>
                <div className="text-sm text-slate-300">{item.selectedDescription || item.poDescription}</div>
              </div>

              {/* PO line number — shown only when the PO carried one (EDI customers
                  require it). Sourced from the leftmost SEQ/line column on the PO. */}
              {item.poLineNumber && (
                <div className="mt-1 text-xs">
                  <span className="text-slate-500 uppercase tracking-wider">PO Line #:</span>{' '}
                  <span className="font-mono text-slate-300">{item.poLineNumber}</span>
                </div>
              )}

              {/* Inventory — three states: in stock / out of stock (set up, backorder) /
                  not set up in ITEMBAL. "Set up" is ITBAL row existence, not stock.
                  Applies to drop-ship lines too — per Rhea (2026-08-21), A+ requires the
                  ITBAL row to exist for the item+warehouse regardless of drop-ship; only
                  on-hand quantity is irrelevant for drop-ship, which this already ignores.
                  A prior version showed a neutral "stock check not required" note here for
                  drop-ship lines with no ITBAL row — that read as reassuring but was wrong,
                  and let order AI1GU (PO 4582225960) show success in the app while A+
                  rejected it with "713: This item is not stocked in this warehouse". */}
              {item.selectedItemNumber && (
                <div className="mt-1.5 flex items-center gap-2">
                  {isInItembal && onHand > 0 ? (
                    <>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">
                        {`${Number(onHand).toLocaleString()} ${item.unitOfMeasure || ''} in stock`.trim()}
                      </span>
                      {item.inventory?.warehouse_id && (
                        <span className="text-xs text-slate-500">WH: {item.inventory.warehouse_id}</span>
                      )}
                    </>
                  ) : isInItembal ? (
                    // Set up in ITEMBAL but 0 on hand: allowed — A+ backorders. Not the red error.
                    <>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">
                        Out of stock — will backorder
                      </span>
                      {item.inventory?.warehouse_id && (
                        <span className="text-xs text-slate-500">WH: {item.inventory.warehouse_id}</span>
                      )}
                    </>
                  ) : itembalUnknown ? (
                    // The ITBAL lookup failed, so setup is genuinely unknown — say that plainly
                    // rather than asserting "not set up", and don't block submit. A+ is still
                    // protected: _check_itembal re-queries ITBAL at submit and rejects for real.
                    <span className="text-xs px-1.5 py-0.5 rounded bg-slate-600/40 text-slate-300 flex items-center gap-1.5">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                      Inventory unavailable — setup not verified
                    </span>
                  ) : (
                    <span className="text-sm px-2 py-1 rounded bg-red-500/20 text-red-400 font-bold uppercase tracking-wide flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      NOT SET UP IN ITEMBAL
                    </span>
                  )}
                </div>
              )}

              {/* Drop-ship vendor — only for drop-ship lines (per-line or whole-order),
                  display + validate only; not sent to A+ on the order. */}
              {(item.isDropShip || headerDropShip) && item.selectedItemNumber && (
                <div className="mt-2 bg-slate-900/50 rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                      <Truck className="w-3 h-3" />
                      Drop-Ship Vendor
                    </span>
                    {vendorEditing[item._id] === undefined && item.vendorResolved && (
                      <button
                        onClick={() => {
                          setVendorError(prev => dropObjKey(prev, item._id));
                          setVendorEditing(prev => ({ ...prev, [item._id]: item.vendorNumber || '' }));
                        }}
                        className="text-xs text-slate-500 hover:text-amber-400 transition-colors"
                      >
                        Change
                      </button>
                    )}
                  </div>

                  {vendorEditing[item._id] !== undefined ? (
                    <div>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          autoFocus
                          placeholder="Vendor #"
                          value={vendorEditing[item._id]}
                          disabled={!!vendorChecking[item._id]}
                          className="w-32 px-2 py-1 bg-slate-700 border border-amber-500/40 rounded text-sm text-white font-mono placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30 disabled:opacity-50"
                          onChange={e => {
                            const cleaned = e.target.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase().slice(0, 6);
                            setVendorEditing(prev => ({ ...prev, [item._id]: cleaned }));
                            setVendorError(prev => dropObjKey(prev, item._id));
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') commitVendorEdit(item);
                            else if (e.key === 'Escape') {
                              vendorEscapedRef.current = true;
                              setVendorEditing(prev => dropObjKey(prev, item._id));
                              setVendorError(prev => dropObjKey(prev, item._id));
                            }
                          }}
                          onBlur={() => {
                            if (vendorEscapedRef.current) { vendorEscapedRef.current = false; return; }
                            commitVendorEdit(item);
                          }}
                        />
                        <button
                          onClick={() => commitVendorEdit(item)}
                          disabled={!!vendorChecking[item._id] || !(vendorEditing[item._id] || '').trim()}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          {vendorChecking[item._id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Save
                        </button>
                        <button
                          onClick={() => {
                            vendorEscapedRef.current = true;
                            setVendorEditing(prev => dropObjKey(prev, item._id));
                            setVendorError(prev => dropObjKey(prev, item._id));
                          }}
                          className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                      {vendorError[item._id] && (
                        <div className="mt-1 text-xs text-red-400 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                          {vendorError[item._id]}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm">
                      {!item.vendorResolved ? (
                        <span className="text-slate-400 flex items-center gap-2">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Looking up vendor…
                        </span>
                      ) : item.vendorNumber ? (
                        <span className="text-white">
                          <span className="font-mono">{item.vendorNumber}</span>
                          {item.vendorName ? <span className="text-slate-300"> — {item.vendorName}</span> : null}
                        </span>
                      ) : (
                        <span className="text-amber-400/80">No vendor on file — click Change to set one</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Action bar — only for non-high-confidence items */}
              {!isHighConfidence && (
                <div className="flex items-center gap-2 mt-2">
                  {altCount > 0 && (
                    <button
                      onClick={() => setItemDropdowns(prev => ({ ...prev, [index]: !prev[index] }))}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        itemDropdowns[index]
                          ? 'bg-slate-600 text-white border border-slate-500'
                          : 'bg-slate-700/50 text-slate-300 border border-slate-600 hover:bg-slate-700 hover:text-white hover:border-slate-500'
                      }`}
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${itemDropdowns[index] ? 'rotate-180' : ''}`} />
                      {altCount} {altCount === 1 ? 'Alternative' : 'Alternatives'}
                    </button>
                  )}
                  <button
                    onClick={() => { trackEvent('csr_item_search', { correlationId, lineIndex: index }); setSearchModal({ id: item._id }); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isUnmatched && altCount === 0
                        ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 hover:shadow-lg hover:shadow-amber-500/20'
                        : 'bg-slate-700/50 text-slate-300 border border-slate-600 hover:bg-slate-700 hover:text-white hover:border-slate-500'
                    }`}
                  >
                    <Search className="w-3.5 h-3.5" />
                    Search A+ Catalog
                  </button>
                </div>
              )}

              {/* Alternatives panel — normal document flow, slides down */}
              {!isHighConfidence && itemDropdowns[index] && altCount > 0 && (
                <div className="mt-2 bg-slate-900/40 rounded-lg border border-slate-700/50 overflow-hidden">
                  {filteredAlts.map((alt, altIdx) => (
                    <button
                      key={altIdx}
                      onClick={() => {
                        onUpdateItemSelection(index, alt);
                        setItemDropdowns(prev => ({ ...prev, [index]: false }));
                      }}
                      className="w-full text-left px-3 py-2.5 hover:bg-slate-700/60 transition-colors border-b border-slate-700/30 last:border-0 group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-white text-sm">{alt.item_number}</span>
                            {alt.match_source === 'order_pad' && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-medium flex-shrink-0">Previously Ordered</span>
                            )}
                            <span className="text-slate-400 text-sm truncate">{alt.description}</span>
                          </div>
                          <div className="mt-0.5">
                            {alt.inventory ? (
                              <span className={`text-xs ${
                                alt.inventory.quantity_on_hand > 0 ? 'text-emerald-500' : 'text-red-400'
                              }`}>
                                {alt.inventory.quantity_on_hand > 0
                                  ? `${Number(alt.inventory.quantity_on_hand).toLocaleString()} ${alt.unit_of_measure || ''} in stock`.trim()
                                  : 'Out of stock'}
                              </span>
                            ) : (
                              <span className="text-xs text-red-400 font-medium">Not Set up in ITEMBAL</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                          <span className="text-xs text-slate-500">{Math.round(alt.confidence * 100)}%</span>
                          <span className="text-xs text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity">Select</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {matchedByDescription ? (
                item.itemConfirmed ? (
                  // Confirmation is reversible — a misclick would otherwise silently pass the
                  // gate this feature exists to enforce. Undo restores the amber confirm banner.
                  <div className="mt-2 text-xs text-emerald-400 flex items-center gap-2">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Item confirmed
                    </span>
                    <button
                      onClick={() => onConfirmItem(item._id, false)}
                      className="text-slate-400 hover:text-amber-400 underline decoration-dotted transition-colors"
                    >
                      Undo
                    </button>
                  </div>
                ) : (
                  // b1: this line was matched by DESCRIPTION (a guess), not by item number or
                  // cross-reference. Make the CSR confirm it's the right A+ item before submit.
                  <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2">
                    <div className="text-xs text-amber-300 flex items-center gap-1.5 font-medium">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      Matched by description, not by item number — confirm this is the right item.
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        onClick={() => onConfirmItem(item._id)}
                        className="px-3 py-1 rounded-lg font-medium text-xs flex items-center gap-1.5 bg-gradient-to-r from-emerald-400 to-emerald-500 text-slate-900 hover:shadow-lg hover:shadow-emerald-500/20 transition-all"
                      >
                        <CheckCircle className="w-3.5 h-3.5" /> Confirm item
                      </button>
                      <button
                        onClick={() => { trackEvent('csr_item_search', { correlationId, lineIndex: index }); setSearchModal({ id: item._id }); }}
                        className="text-xs text-slate-400 hover:text-amber-400 transition-colors"
                      >
                        Wrong item? Change
                      </button>
                    </div>
                  </div>
                )
              ) : item.issue ? (
                <div className="mt-2 text-xs text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {item.issue}
                </div>
              ) : null}

              {item.extQuantity == null && item.poQuantity == null && !item.resolution && (() => {
                const commitQty = () => {
                  const raw = qtyInputs[index] || '';
                  const val = parseFloat(raw);
                  if (!isNaN(val) && val > 0) {
                    onUpdateItemQuantity(index, val);
                  }
                };
                return (
                  <div className="mt-2 flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                    <span className="text-xs text-amber-400 whitespace-nowrap">Qty not on PO</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Enter qty"
                      value={qtyInputs[index] ?? (item.poQuantity != null ? String(item.poQuantity) : '')}
                      className="w-24 px-2 py-1 bg-slate-700 border border-amber-500/40 rounded text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30"
                      onChange={e => {
                        const raw = e.target.value.replace(/[^0-9.]/g, '');
                        setQtyInputs(prev => ({ ...prev, [index]: raw }));
                      }}
                      onKeyDown={e => { if (e.key === 'Enter') commitQty(); }}
                    />
                    {item.poUnitOfMeasure && (
                      <span className="text-xs text-slate-400">{item.poUnitOfMeasure}</span>
                    )}
                    <button
                      onClick={commitQty}
                      disabled={!qtyInputs[index] || isNaN(parseFloat(qtyInputs[index]))}
                      className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <Check className="w-3 h-3" />
                      Update
                    </button>
                  </div>
                );
              })()}

              <div className="mt-2 bg-slate-900/50 rounded-lg px-3 py-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {item.priceStockUomResolved
                      ? `A+ CONTRACT PRICING (STOCK UOM ${item.priceStockUomLabel})`
                      : 'A+ Contract Pricing'}
                  </span>
                  {item.resolution ? (
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                  ) : (
                    <>
                      {item.priceVarianceLevel === 'exact' && <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />}
                      {item.priceVarianceLevel === 'warning' && <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
                      {item.priceVarianceLevel === 'invalid' && <XCircle className="w-3.5 h-3.5 text-red-400" />}
                      {!item.priceVarianceLevel && <XCircle className="w-3.5 h-3.5 text-slate-600" />}
                    </>
                  )}
                </div>
                {item.resolution ? (
                  <div className="text-sm text-white flex items-center justify-between">
                    <span>
                      {item.resolution.quantity} {item.resolution.uom} @ {formatUnitPrice(item.resolution.unitPrice)}
                    </span>
                    <span className="font-medium">
                      = {formatCurrency(item.resolution.quantity * item.resolution.unitPrice)}
                    </span>
                  </div>
                ) : item.priceStockUomResolved && item.priceStockUnitPrice != null ? (
                  <div className="text-sm text-white">
                    {renderEditableQty(item.poQuantity, item.poUnitOfMeasure)} @ {formatUnitPrice(item.priceStockUnitPrice)} = {formatCurrency(item.poQuantity * item.priceStockUnitPrice)}
                  </div>
                ) : item.valUnitPrice != null ? (
                  uomMismatch ? (
                    // UOM mismatch (e.g. PO in EA but item priced per M): the backend deliberately
                    // does NOT resolve a conversion here, so don't fabricate a same-unit total — it
                    // would be wrong (5000 EA × $374.78/M rendered as $1,873,900 instead of the real
                    // $1,873.90). Show the contract price in its pricing UOM; the Resolve UOM Mismatch
                    // step converts and sets the true line total.
                    <div className="text-sm text-white">
                      {formatUnitPrice(item.valUnitPrice)}
                      <span className="text-slate-400"> / {priceUom} · resolve UOM mismatch to set the line total</span>
                    </div>
                  ) : (
                    <div className="text-sm text-white">
                      {renderEditableQty(item.poQuantity, item.poUnitOfMeasure)} @ {formatUnitPrice(item.valUnitPrice)} = {formatCurrency(contractQty * item.valUnitPrice)}
                    </div>
                  )
                ) : item.priceIssue === 'Validating pricing...' ? (
                  <div className="text-sm text-slate-400 flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Validating pricing...
                  </div>
                ) : (
                  <div className="text-sm text-slate-500">No Contract Pricing</div>
                )}
                {item.priceIssue && !item.resolution && (
                  <div className="text-xs text-amber-400/80 mt-1">{item.priceIssue}</div>
                )}
                {uomMismatch && !item.resolution && (
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={() => setResolutionModal({ id: item._id, mode: 'uom_mismatch' })}
                      className="px-3 py-1 rounded-lg font-medium text-sm flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 hover:shadow-lg hover:shadow-amber-500/20 transition-all"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Resolve UOM Mismatch
                    </button>
                  </div>
                )}
                {uomMismatch && item.resolution && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-emerald-400 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      UOM Resolved ({item.resolution.type === 'po' ? 'PO Values' : item.resolution.type === 'contract' ? 'A+ Contract Pricing' : item.resolution.type === 'stock_conversion' ? 'Stock UOM' : item.resolution.type === 'stocking_conversion' ? `Stocking UOM (${item.resolution.uom})` : 'Custom'})
                    </span>
                    <button
                      onClick={() => setResolutionModal({ id: item._id, mode: 'uom_mismatch' })}
                      className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                )}
                {item.resolution && item.resolution.type === 'uom_auto_1to1' && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-emerald-400 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      UOM auto-matched ({(item.poUnitOfMeasure || '').toUpperCase()} → {item.resolution.uom}, 1:1)
                    </span>
                    <button
                      onClick={() => setResolutionModal({ id: item._id, mode: 'uom_mismatch' })}
                      className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                )}
                {hasPriceVariance && (!item.resolution || item.resolution.type === 'uom_auto_1to1') && (
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={() => setResolutionModal({ id: item._id, mode: 'price_variance' })}
                      className="px-3 py-1 rounded-lg font-medium text-sm flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 hover:shadow-lg hover:shadow-amber-500/20 transition-all"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Resolve Price Variance
                    </button>
                  </div>
                )}
                {hasPriceVariance && item.resolution && item.resolution.type !== 'uom_auto_1to1' && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-emerald-400 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Price Resolved ({item.resolution.type === 'po' ? 'PO Price' : item.resolution.type === 'contract' ? 'Contract Price' : 'Custom'})
                    </span>
                    <button
                      onClick={() => setResolutionModal({ id: item._id, mode: 'price_variance' })}
                      className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                )}
                {hasInvalidStockingUom && !item.resolution && (
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={() => setResolutionModal({ id: item._id, mode: 'uom_mismatch' })}
                      className="px-3 py-1 rounded-lg font-medium text-sm flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 hover:shadow-lg hover:shadow-amber-500/20 transition-all"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Resolve Stocking UOM
                    </button>
                  </div>
                )}
                {hasInvalidStockingUom && item.resolution && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-emerald-400 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      UOM Resolved ({item.resolution.uom})
                    </span>
                    <button
                      onClick={() => setResolutionModal({ id: item._id, mode: 'uom_mismatch' })}
                      className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* CSR-added line with no contract price: give an explicit price control.
                    Without this the line strands as "pricing not yet resolved" with no button
                    to click (the UOM/variance cases above have their own buttons; guard against
                    double-rendering here). */}
                {item.addedByCsr && !uomMismatch && !hasPriceVariance && !hasInvalidStockingUom && !item.resolution && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-amber-400/80">New line — set a price to continue</span>
                    <button
                      onClick={() => setResolutionModal({ id: item._id, mode: 'manual_price' })}
                      className="px-3 py-1 rounded-lg font-medium text-sm flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 hover:shadow-lg hover:shadow-amber-500/20 transition-all"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Set Price
                    </button>
                  </div>
                )}
                {item.addedByCsr && !uomMismatch && !hasPriceVariance && !hasInvalidStockingUom && item.resolution && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-emerald-400 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Price set (manually added line)
                    </span>
                    <button
                      onClick={() => setResolutionModal({ id: item._id, mode: 'manual_price' })}
                      className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>

              {!item.addedByCsr && (
              <div className={`mt-2 pt-2 border-t border-slate-600/50 flex items-center justify-between text-sm ${item.resolution || (item.extQuantity == null && item.poQuantity != null) || (item.extQuantity != null && item.poQuantity !== item.extQuantity) ? 'opacity-40' : ''}`}>
                <div className="text-slate-500 text-xs">
                  PO Item: {item.poItemNumber} - {item.poDescription}
                </div>
                <div className="text-slate-400">
                  PO Qty: <span className={item.resolution ? 'text-slate-500' : 'text-white'}>{poQtyDisplay ?? '—'}</span>
                  {item.poUnitOfMeasure && <span className="text-slate-500 ml-1">{item.poUnitOfMeasure}</span>}
                  {item.extUnitPrice && (
                    <span className="ml-2">
                      @ <span className={item.resolution ? 'text-slate-500' : 'text-white'}>{formatUnitPrice(item.extUnitPrice)}</span> = <span className={item.resolution ? 'text-slate-500' : 'text-white font-medium'}>{formatCurrency(item.extTotal || (poQtyDisplay ? poQtyDisplay * item.extUnitPrice : 0))}</span>
                    </span>
                  )}
                </div>
              </div>
              )}

              {/* Note — persistent display when saved, input when editing */}
              {item.note && noteEditing[index] === undefined && (
                <div className="mt-2 flex items-center gap-2 bg-slate-700/30 rounded-lg px-3 py-1.5">
                  <StickyNote className="w-3 h-3 text-amber-400 flex-shrink-0" />
                  <span className="text-sm text-slate-300 flex-1 truncate">"{item.note}"</span>
                  <select
                    value={item.notePrintCode || 'both'}
                    onChange={(e) => onUpdateNotePrintCode(index, e.target.value)}
                    title="Where this comment prints"
                    className={`text-xs rounded px-1.5 py-0.5 border focus:border-amber-500 focus:outline-none flex-shrink-0 ${
                      item.notePrintCode === 'internal'
                        ? 'bg-amber-500/10 border-amber-500/50 text-amber-400'
                        : 'bg-slate-700 border-slate-600 text-slate-300'
                    }`}
                  >
                    {PRINT_CODES.map((pc) => (
                      <option key={pc.value} value={pc.value}>{pc.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => setNoteEditing(prev => ({ ...prev, [index]: item.note }))}
                    className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onUpdateNote(index, '')}
                    className="text-slate-600 hover:text-red-400 transition-colors"
                    title="Remove line comment"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              {noteEditing[index] !== undefined && (
                <div className="mt-2 flex items-center gap-2 bg-slate-700/30 rounded-lg px-3 py-1.5">
                  <StickyNote className="w-3 h-3 text-amber-400 flex-shrink-0" />
                  <input
                    type="text"
                    maxLength={62}
                    autoFocus
                    placeholder="Add line comment (62 char max)"
                    value={noteEditing[index]}
                    className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30"
                    onChange={e => {
                      const cleaned = sanitizeErpText(e.target.value, 62);
                      setNoteEditing(prev => ({ ...prev, [index]: cleaned }));
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        onUpdateNote(index, noteEditing[index].trim());
                        setNoteEditing(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== String(index))));
                      } else if (e.key === 'Escape') {
                        noteEscapedRef.current = true;
                        setNoteEditing(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== String(index))));
                      }
                    }}
                    onBlur={() => {
                      if (noteEscapedRef.current) { noteEscapedRef.current = false; return; }
                      onUpdateNote(index, noteEditing[index].trim());
                      setNoteEditing(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== String(index))));
                    }}
                  />
                  <span className="text-xs text-slate-500 tabular-nums w-10 text-right flex-shrink-0">
                    {(noteEditing[index] || '').length}/62
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {resolutionModal && (() => {
        // Resolve the current index from the stable id at both render and callback time,
        // so a line deletion while the modal is open can't target the wrong item.
        const idx = selectedItems.findIndex(it => it._id === resolutionModal.id);
        if (idx === -1) return null;
        return (
          <PriceResolutionModal
            item={selectedItems[idx]}
            itemIndex={idx}
            mode={resolutionModal.mode}
            onApply={(_i, resolution) => {
              const cur = selectedItems.findIndex(it => it._id === resolutionModal.id);
              if (cur !== -1) onResolvePrice(cur, resolution);
              setResolutionModal(null);
            }}
            onClose={() => setResolutionModal(null)}
          />
        );
      })()}

      {searchModal && (() => {
        const idx = selectedItems.findIndex(it => it._id === searchModal.id);
        if (idx === -1) return null;
        return (
          <ItemSearchModal
            item={selectedItems[idx]}
            itemIndex={idx}
            customerNumber={customerNumber}
            shiptoNumber={selectedShipTo?.number || null}
            warehouseId={warehouseId}
            correlationId={correlationId}
            user={user}
            onSelect={(_i, selectedItem) => {
              const cur = selectedItems.findIndex(it => it._id === searchModal.id);
              if (cur !== -1) onUpdateItemSelection(cur, selectedItem);
              setSearchModal(null);
            }}
            onClose={() => setSearchModal(null)}
          />
        );
      })()}

      {addLineModal && (
        <AddLineItemModal
          selectedItems={selectedItems}
          customerNumber={selectedCustomer?.number || customerNumber}
          shipToNumber={selectedShipTo?.number || null}
          warehouseId={warehouseId}
          correlationId={correlationId}
          user={user}
          onAdd={(data) => {
            onAddLineItem(data);
            setAddLineModal(false);
          }}
          onClose={() => setAddLineModal(false)}
        />
      )}
    </div>
  );
}
